// Loaded once by theme.liquid. No guide request runs until a launcher is clicked.
const config = document.getElementById('bt-sizing-guide-config');
const status = document.getElementById('bt-sizing-guide-status');
const drawerId = 'blacktie-find-your-fit-drawer';
let guideRequest;
let busy = false;
let cancelled = false;

function initializeGuide(drawer) {
  if (drawer.dataset.btFitReady === 'true') return;
  const root = drawer.querySelector('[data-bt-fit-root]');
  if (!root) throw new Error('Sizing guide content is missing.');
  drawer.dataset.btFitReady = 'true';
  function showPanel(name) {
    const target = name
      ? Array.from(root.querySelectorAll('[data-bt-panel]')).find(panel => panel.dataset.btPanel === name)
      : root.querySelector('.bt-fit-home');
    if (!target) return;
    root.querySelectorAll('.bt-fit-panel').forEach(panel => { panel.style.display = 'none'; });
    target.style.display = 'block';
    root.scrollTop = 0;
    const back = target.querySelector('.bt-fit-back');
    if (back) back.focus({ preventScroll: true });
    else if (name === null && drawer.hasAttribute('open')) {
      target.querySelector('button')?.focus({ preventScroll: true });
    }
  }
  root.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const open = event.target.closest('[data-bt-open]');
    if (open) {
      event.preventDefault();
      showPanel(open.getAttribute('data-bt-open'));
    } else if (event.target.closest('[data-bt-home]')) {
      event.preventDefault();
      showPanel(null);
    }
  });
  drawer.querySelector('dialog').addEventListener('close', () => showPanel(null));
}

async function loadGuide() {
  const existing = document.getElementById(drawerId);
  if (existing) {
    if (typeof existing.open !== 'function') throw new Error('Drawer is not ready. Please try again.');
    initializeGuide(existing);
    return existing;
  }
  if (guideRequest) return guideRequest;
  guideRequest = (async () => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    try {
      const url = new URL(config.dataset.source, window.location.origin);
      // Retain explicit draft preview context; normal preview cookies also accompany the request.
      const previewId = new URL(window.location.href).searchParams.get('preview_theme_id');
      if (previewId) url.searchParams.set('preview_theme_id', previewId);
      const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error('Unable to fetch guide.');
      const html = await response.text();
      if (!customElements.get('theme-drawer')) throw new Error('Drawer component is unavailable.');
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const template = parsed.querySelector('template[data-bt-sizing-guide-template]');
      const fragment = template?.content;
      const drawer = fragment?.querySelector('#' + drawerId);
      if (!drawer || !drawer.querySelector('[data-bt-fit-root]')) throw new Error('Invalid guide response.');
      // Fetched inline scripts are deliberately not executed. Initialize the guide explicitly.
      fragment.querySelectorAll('script').forEach(script => script.remove());
      const mount = document.createElement('div');
      mount.id = 'bt-sizing-guide-mount';
      mount.append(document.importNode(fragment, true));
      const importedDrawer = mount.querySelector('#' + drawerId);
      initializeGuide(importedDrawer);
      document.body.append(mount);
      return importedDrawer;
    } finally {
      clearTimeout(timeout);
    }
  })();
  try {
    return await guideRequest;
  } catch (error) {
    guideRequest = undefined; // A subsequent click retries rather than caching a failure.
    throw error;
  }
}

// HeaderDrawer.close() returns before its animation/focus cleanup finishes.
// Wait for its root details to lose [open], which happens in that cleanup.
function closeMobileMenu(menu) {
  const details = menu.querySelector('details[ref="details"]');
  if (!details || !details.open) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const observer = new MutationObserver(() => {
      if (!details.open) {
        cleanup();
        resolve();
      }
    });
    const timeout = setTimeout(() => {
      cleanup();
      reject(new Error('Menu did not finish closing.'));
    }, 2500);
    function cleanup() {
      observer.disconnect();
      clearTimeout(timeout);
    }
    observer.observe(details, { attributes:true, attributeFilter:['open'] });
    menu.close();
    if (!details.open) {
      cleanup();
      resolve();
    }
  });
}

async function handOffMenuFocus(trigger) {
  const mobileMenu = trigger.closest('header-drawer');
  if (mobileMenu) {
    const summary = mobileMenu.querySelector('details[ref="details"] > summary');
    await closeMobileMenu(mobileMenu);
    if (cancelled) return null;
    // Return to a visible control when the guide closes, not a hidden menu button.
    summary?.focus({ preventScroll:true });
    return summary;
  }
  const desktopMenu = trigger.closest('header-menu');
  if (desktopMenu) {
    if (typeof desktopMenu.closeForSizingGuide !== 'function') {
      throw new Error('Install the updated header-menu.js first.');
    }
    const topLink = desktopMenu.querySelector('[ref="menuitem"][aria-expanded="true"]');
    topLink?.focus({ preventScroll:true });
    desktopMenu.closeForSizingGuide();
    return topLink;
  }
  return trigger;
}

function showFailure() {
  status.replaceChildren(document.createTextNode('The guide could not load. Please try again, or '));
  const link = document.createElement('a');
  link.href = config.dataset.fallback;
  link.textContent = 'visit Find Your Fit';
  const dismiss = document.createElement('button');
  dismiss.type = 'button';
  dismiss.textContent = 'Dismiss';
  dismiss.addEventListener('click', () => { status.hidden = true; });
  status.append(link, dismiss);
  status.hidden = false;
}

if (config && status) {
  document.addEventListener('keydown', event => {
    if (busy && event.key === 'Escape') {
      cancelled = true;
      status.hidden = true;
    }
  });
  document.addEventListener('click', async event => {
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest('[data-bt-sizing-guide-open]');
    if (!trigger) return;
    event.preventDefault();
    if (busy) return;
    busy = true;
    cancelled = false;
    trigger.setAttribute('aria-busy', 'true');
    trigger.setAttribute('aria-disabled', 'true');
    // Explicit focus also supports browsers that do not focus buttons on pointer clicks.
    trigger.focus({ preventScroll: true });
    status.textContent = 'Loading sizing guide…';
    status.hidden = false;
    try {
      const drawer = await loadGuide();
      status.hidden = true;
      if (!cancelled && trigger.isConnected && document.activeElement === trigger) {
        const returnTarget = await handOffMenuFocus(trigger);
        if (!cancelled && returnTarget?.isConnected) drawer.open();
      }
    } catch (error) {
      if (!cancelled) showFailure();
    } finally {
      busy = false;
      trigger.removeAttribute('aria-busy');
      trigger.removeAttribute('aria-disabled');
    }
  });
}
